/**
 * BlurUpImage Component
 * Displays images with blur-up lazy loading effect
 * - Shows tiny blurred placeholder while loading
 * - Replaces with full-quality image when ready
 * - Handles missing blur images gracefully
 */

import React, { useState, useEffect, useRef } from 'react';
import { getTinyImageUrl } from '../utils/imageUtils';

const BlurUpImage = React.forwardRef(({
  src,
  alt = '',
  className = '',
  style = {},
  onLoad,
  onError,
  objectFit = 'cover',
  objectPosition = 'center',
  lazy = true, // Enable lazy loading by default
  ...props
}, ref) => {
  const [displaySrc, setDisplaySrc] = useState(null);
  const [showBlur, setShowBlur] = useState(true);
  const [isInView, setIsInView] = useState(!lazy); // If lazy is false, load immediately
  const displaySrcRef = useRef(null);
  const imgRef = useRef(null);
  const observerRef = useRef(null);

  // Lazy loading with Intersection Observer
  useEffect(() => {
    if (!lazy || isInView) return;

    const currentImg = imgRef.current;
    if (!currentImg) return;

    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsInView(true);
            if (observerRef.current) {
              observerRef.current.disconnect();
            }
          }
        });
      },
      {
        rootMargin: '50px', // Start loading 50px before the image enters viewport
        threshold: 0.01
      }
    );

    observerRef.current.observe(currentImg);

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [lazy, isInView]);

  useEffect(() => {
    if (!src || !isInView) {
      if (!isInView) {
        setShowBlur(true);
      }
      return;
    }

    // Generate blur image URL
    const blurUrl = getTinyImageUrl(src);
    let isMounted = true;

    // Load blur image first
    if (blurUrl && blurUrl !== src) {
      const blurImg = new Image();

      blurImg.onload = () => {
        if (isMounted) {
          displaySrcRef.current = blurUrl;
          setDisplaySrc(blurUrl);
          // Start loading full image
          loadFullImage();
        }
      };

      blurImg.onerror = () => {
        // Blur failed, try loading full image directly
        if (isMounted) {
          loadFullImage();
        }
      };

      blurImg.src = blurUrl;
    } else {
      // No blur URL or same as original, load full image directly
      loadFullImage();
    }

    function loadFullImage() {
      if (!isMounted) return;

      const fullImg = new Image();

      fullImg.onload = () => {
        if (isMounted) {
          displaySrcRef.current = src;
          setDisplaySrc(src);
          setShowBlur(false);
          onLoad?.(fullImg);
        }
      };

      fullImg.onerror = (err) => {
        if (isMounted) {
          onError?.(err);
          // Keep showing blur if full image fails
          if (blurUrl && displaySrcRef.current === blurUrl) {
            setShowBlur(true);
          } else {
            displaySrcRef.current = src;
            setDisplaySrc(src);
          }
        }
      };

      fullImg.src = src;
    }

    // Cleanup
    return () => {
      isMounted = false;
    };
  }, [src, onLoad, onError, isInView]);

  const finalSrc = displaySrc || (isInView ? src : '');

  return (
    <img
      ref={(node) => {
        imgRef.current = node;
        if (typeof ref === 'function') {
          ref(node);
        } else if (ref) {
          ref.current = node;
        }
      }}
      src={finalSrc}
      alt={alt}
      className={className}
      loading={lazy ? 'lazy' : 'eager'}
      decoding="async"
      style={{
        objectFit,
        objectPosition,
        transition: showBlur ? 'filter 0.3s ease-out' : 'none',
        filter: showBlur ? 'blur(12px)' : 'blur(0px)',
        backgroundColor: '#f0f0f0', // Placeholder background
        ...style,
      }}
      {...props}
    />
  );
});

BlurUpImage.displayName = 'BlurUpImage';

export default BlurUpImage;
